import { SpeakerWaveIcon } from '@heroicons/react/24/outline';
import {
  VoiceAgentConfigurationCallBehaviorRapportBuilding,
  VoiceAgentConfigurationCallBehaviorRequiredContactInfoType,
  VoiceAgentConfigurationModelVoice,
  VoiceAgentLanguage,
  VoiceAgentStartSpeaker
} from '~/api/graphql';
import { ProLabel } from '~/components/pro-label';
import { Button } from '@oysterjs/ui/button';
import { Combobox } from '@oysterjs/ui/combobox';
import { Checkbox, CheckboxField, CheckboxGroup } from '@oysterjs/ui/checkbox';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { MultiListbox, MultiListboxLabel, MultiListboxOption } from '@oysterjs/ui/multiselect';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { Select } from '@oysterjs/ui/select';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { Textarea } from '@oysterjs/ui/textarea';
import { VoiceDiscreteSlider, type VoiceSliderOption } from './voice-discrete-slider';
import {
  REQUIRED_CONTACT_INFO_LINE_OF_BUSINESS,
  transferDirectoryUsesLineOfBusinessRouting
} from './voice_agent_form_input';
import {
  responsivenessOptions,
  voiceSpeedOptions,
  voiceTemperatureOptions,
  voiceVolumeOptions
} from './voice-model-options';
import { VoiceAgentFormSectionProps } from './voice-agent-section-props';
import { applyLanguageSelectionChange, applyVoiceSelectionChange } from './voice_language_support';

export const requiredContactInfoOptions = [
  {
    value: VoiceAgentConfigurationCallBehaviorRequiredContactInfoType.Email,
    label: 'Email Address',
    description: "Caller's email address for follow-up communication."
  },
  {
    value: VoiceAgentConfigurationCallBehaviorRequiredContactInfoType.Phone,
    label: 'Phone Number',
    description:
      "Caller's phone number for callback purposes. If selected Cara will ask the customer for their contact number; otherwise, the inbound phone number will be saved."
  },
  {
    value: REQUIRED_CONTACT_INFO_LINE_OF_BUSINESS,
    label: 'Line of Business',
    description: "The caller's insurance line of business for transfer routing."
  }
];

export const beginAfterUserSilenceOptions: VoiceSliderOption[] = [
  { value: 0, label: 'None' },
  { value: 1000, label: '1s' },
  { value: 2000, label: '' },
  { value: 3000, label: '' },
  { value: 4000, label: '' },
  { value: 5000, label: '5s' },
  { value: 6000, label: '' },
  { value: 7000, label: '' },
  { value: 8000, label: '' },
  { value: 9000, label: '' },
  { value: 10000, label: '10s' }
];

export const VoiceAgentBasicInformationSection = ({
  form,
  phoneNumber,
  validationErrors,
  getFieldId,
  getValidationError,
  updateForm,
  filteredLanguageOptions,
  languageSupport,
  isOutboundVoice = false,
  supportsMultipleLanguages = false,
  outboundIntroComplianceDisclosure
}: VoiceAgentFormSectionProps) => (
  <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
    <div className="px-4 py-6 sm:px-6">
      <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
        <div>
          <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
            Basic Information
          </h4>
          <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
            Basic identification and behavioral information for your voice agent.
          </p>
          {!isOutboundVoice && (
            <>
              <br />
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                <b>Recording Notice:</b> Some states require a disclosure notice for recording phone
                calls. You are responsible for setting this up on your telephony system or adding
                the notice into the Introduction Phrase. If you have a pre-recorded message setup in
                your telephony system, then you may need to configure the Start Speaker variable to
                "User" in the Call Behavior section below to wait for your pre-recorded notice to
                finish before the AI begins speaking.
              </p>
            </>
          )}
          {isOutboundVoice && (
            <>
              <br />
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                <b>AI &amp; Recording Disclosure:</b> Cara always ends your introduction with a
                short notice that the call is AI-assisted and recorded. That notice is required for
                compliance and cannot be changed.
              </p>
            </>
          )}
        </div>

        <Fieldset className="flex flex-col gap-y-6 col-span-2">
          <FieldGroup>
            <Field className="max-w-md" data-field-id={getFieldId(['name'])}>
              <Label>Name</Label>
              <Description>A descriptive name for this voice agent configuration.</Description>
              <Input
                value={form.name}
                onChange={(e) => {
                  const name = e.currentTarget.value;
                  updateForm(['name'], (prev) => ({ ...prev, name }));
                }}
                invalid={!!getValidationError(validationErrors, ['name'])}
                placeholder="Customer Service Agent"
              />
              {!!getValidationError(validationErrors, ['name']) && (
                <ErrorMessage>{getValidationError(validationErrors, ['name'])}</ErrorMessage>
              )}
            </Field>
            <Field className="max-w-md" data-field-id={getFieldId(['name'])}>
              <Label>Phone Number</Label>
              <Description>
                The phone number for this voice agent. You cannot change this number.
              </Description>
              <Input value={phoneNumber} disabled />
            </Field>
            <Field className="max-w-md" data-field-id={getFieldId(['configuration', 'agentName'])}>
              <Label>Agent Name Override</Label>
              <Description>
                {isOutboundVoice
                  ? 'The name used when Cara introduces herself on outbound calls (e.g. "This is Cara from…"). Leave blank to use \'Cara\'.'
                  : 'The name by which Cara introduces herself to the caller.'}
              </Description>
              <Input
                value={form.configuration?.agentName || ''}
                onChange={(e) => {
                  const agentName = e.currentTarget.value;
                  updateForm(['configuration', 'agentName'], (prev) => ({
                    ...prev,
                    configuration: { ...prev.configuration, agentName }
                  }));
                }}
                invalid={!!getValidationError(validationErrors, ['configuration', 'agentName'])}
                placeholder="Leave blank to use 'Cara'"
              />
              {!!getValidationError(validationErrors, ['configuration', 'agentName']) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, ['configuration', 'agentName'])}
                </ErrorMessage>
              )}
            </Field>

            {supportsMultipleLanguages ? (
              <Field data-field-id={getFieldId(['configuration', 'languages'])}>
                <Label>Languages</Label>
                <Description>
                  Choose one language when possible, or only the languages needed, to maximize
                  transcription accuracy. After you add a non-English language, the list narrows to
                  languages the selected voice provider can speak.
                </Description>
                <div className="max-w-md">
                  <MultiListbox
                    value={form.configuration?.languages ?? []}
                    onChange={(requestedLanguages: VoiceAgentLanguage[]) => {
                      const result = applyLanguageSelectionChange(
                        languageSupport,
                        form.configuration?.languages ?? [],
                        form.configuration?.model?.voice ?? VoiceAgentConfigurationModelVoice.Grace,
                        requestedLanguages
                      );
                      updateForm(['configuration', 'languages'], (prev) => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          languages: result.languages,
                          model: {
                            ...prev.configuration.model,
                            voice: result.voice
                          }
                        }
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['configuration', 'languages'])}
                    placeholder="Select one or more languages..."
                  >
                    {filteredLanguageOptions.map((option) => (
                      <MultiListboxOption key={option.value} value={option.value}>
                        <MultiListboxLabel>{option.label}</MultiListboxLabel>
                      </MultiListboxOption>
                    ))}
                  </MultiListbox>
                </div>
                {getValidationError(validationErrors, ['configuration', 'languages']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['configuration', 'languages'])}
                  </ErrorMessage>
                )}
              </Field>
            ) : (
              <Field
                className="max-w-md"
                data-field-id={getFieldId(['configuration', 'languages'])}
              >
                <Label>Language</Label>
                <Description>
                  Primary language for the voice agent to understand and speak.
                </Description>
                <Combobox
                  value={form.configuration?.languages?.[0]}
                  options={filteredLanguageOptions}
                  onChange={(value) => {
                    const newLanguage = value as VoiceAgentLanguage;
                    const result = applyLanguageSelectionChange(
                      languageSupport,
                      form.configuration.languages,
                      form.configuration.model.voice,
                      [newLanguage]
                    );
                    updateForm(['configuration', 'languages'], (prev) => {
                      return {
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          languages: result.languages,
                          model: {
                            ...prev.configuration.model,
                            voice: result.voice
                          }
                        }
                      };
                    });
                  }}
                  error={
                    getValidationError(validationErrors, ['configuration', 'languages']) ||
                    undefined
                  }
                  placeholder="Select a language..."
                />
              </Field>
            )}

            <Field data-field-id={getFieldId(['configuration', 'introPhrase'])}>
              <Label>Introduction Phrase</Label>
              <Description>
                {isOutboundVoice ? (
                  <>
                    Custom greeting that Cara will use when placing outbound calls. Cara will say
                    this phrase exactly, then the required disclosure below. You can input
                    &lt;customer_name&gt; for Cara to say the customer&apos;s name, if available.
                    This is separate from your inbound agent greeting and any out-of-office intro.
                  </>
                ) : (
                  <>
                    Custom greeting that Cara will use when answering calls. Cara will say this
                    phrase exactly. You can input &lt;customer_name&gt; for Cara to say the
                    caller&apos;s name, if the info is available.
                  </>
                )}
              </Description>
              <Textarea
                value={form.configuration?.introPhrase || ''}
                onChange={(e) => {
                  const introPhrase = e.currentTarget.value;
                  updateForm(['configuration', 'introPhrase'], (prev) => ({
                    ...prev,
                    configuration: { ...prev.configuration, introPhrase }
                  }));
                }}
                invalid={!!getValidationError(validationErrors, ['configuration', 'introPhrase'])}
                placeholder={
                  isOutboundVoice
                    ? 'Hi, this is Cara from [Agency Name]. I have an update from your agent. It should only take a minute. Is now a good time?'
                    : 'Hello <customer_name>, this is Cara from [Agency Name]. How can I help you today?'
                }
                style={{ minHeight: '80px' }}
              />
              {!!getValidationError(validationErrors, ['configuration', 'introPhrase']) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, ['configuration', 'introPhrase'])}
                </ErrorMessage>
              )}
            </Field>

            {isOutboundVoice && outboundIntroComplianceDisclosure && (
              <Field>
                <Label>Required Disclosure</Label>
                <Description>
                  Cara always says this right after your introduction for compliance purposes. You
                  cannot change this phrase.
                </Description>
                <Textarea
                  value={outboundIntroComplianceDisclosure}
                  disabled
                  style={{ minHeight: '80px' }}
                />
              </Field>
            )}

            <Field data-field-id={getFieldId(['configuration', 'globalCustomInstructions'])}>
              <Label>Global Custom Facts</Label>
              <Description>
                Additional instructions that apply to all interactions with Cara. Do not add
                information that is already configured via the input fields below.
              </Description>
              <Textarea
                value={form.configuration?.globalCustomInstructions || ''}
                onChange={(e) => {
                  const globalCustomInstructions = e.currentTarget.value;
                  updateForm(['configuration', 'globalCustomInstructions'], (prev) => ({
                    ...prev,
                    configuration: { ...prev.configuration, globalCustomInstructions }
                  }));
                }}
                invalid={
                  !!getValidationError(validationErrors, [
                    'configuration',
                    'globalCustomInstructions'
                  ])
                }
                placeholder="Always be friendly and professional. Remember to..."
                style={{ minHeight: '100px' }}
              />
              {!!getValidationError(validationErrors, [
                'configuration',
                'globalCustomInstructions'
              ]) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, [
                    'configuration',
                    'globalCustomInstructions'
                  ])}
                </ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
      </div>
    </div>
  </div>
);

export const VoiceAgentAgencyInformationSection = ({
  form,
  agencyPlaceholders,
  validationErrors,
  getFieldId,
  getValidationError,
  updateForm
}: VoiceAgentFormSectionProps) => (
  <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
    <div className="px-4 py-6 sm:px-6">
      <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
        <div>
          <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
            Agency Information
          </h4>
          <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
            Change the agency information that Cara should reference over the phone. If any of these
            are not specified, Cara will use your account information.
          </p>
        </div>

        <Fieldset className="flex flex-col gap-y-6 col-span-2">
          <FieldGroup>
            <Field
              className="max-w-md"
              data-field-id={getFieldId(['configuration', 'agency', 'name'])}
            >
              <Label>Agency Name</Label>
              <Description>
                Override the agency name if different from your default setting.
              </Description>
              <Input
                value={form.configuration?.agency?.name || ''}
                onChange={(e) => {
                  const name = e.currentTarget.value;
                  updateForm(['configuration', 'agency', 'name'], (prev) => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      agency: { ...prev.configuration?.agency, name }
                    }
                  }));
                }}
                invalid={
                  !!getValidationError(validationErrors, ['configuration', 'agency', 'name'])
                }
                placeholder={agencyPlaceholders.agencyName}
              />
              {!!getValidationError(validationErrors, ['configuration', 'agency', 'name']) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, ['configuration', 'agency', 'name'])}
                </ErrorMessage>
              )}
            </Field>

            <Field
              className="max-w-md"
              data-field-id={getFieldId(['configuration', 'agency', 'phone'])}
            >
              <Label>Phone Number</Label>
              <Description>
                Your support phone number to give to callers if they ask for it.
              </Description>
              <Input
                type="tel"
                value={form.configuration?.agency?.phone || ''}
                onChange={(e) => {
                  const phone = e.currentTarget.value;
                  updateForm(['configuration', 'agency', 'phone'], (prev) => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      agency: { ...prev.configuration?.agency, phone }
                    }
                  }));
                }}
                invalid={
                  !!getValidationError(validationErrors, ['configuration', 'agency', 'phone'])
                }
                placeholder={agencyPlaceholders.contactPhone}
              />
              {!!getValidationError(validationErrors, ['configuration', 'agency', 'phone']) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, ['configuration', 'agency', 'phone'])}
                </ErrorMessage>
              )}
            </Field>

            <Field
              className="max-w-md"
              data-field-id={getFieldId(['configuration', 'agency', 'email'])}
            >
              <Label>Email Address</Label>
              <Description>
                Your support email address to give to callers if they ask for it.
              </Description>
              <Input
                type="text"
                value={form.configuration?.agency?.email || ''}
                onChange={(e) => {
                  const email = e.currentTarget.value;
                  updateForm(['configuration', 'agency', 'email'], (prev) => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      agency: { ...prev.configuration?.agency, email }
                    }
                  }));
                }}
                invalid={
                  !!getValidationError(validationErrors, ['configuration', 'agency', 'email'])
                }
                placeholder={agencyPlaceholders.contactEmail}
              />
              {!!getValidationError(validationErrors, ['configuration', 'agency', 'email']) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, ['configuration', 'agency', 'email'])}
                </ErrorMessage>
              )}
            </Field>

            <Field
              className="max-w-md"
              data-field-id={getFieldId(['configuration', 'agency', 'address'])}
            >
              <Label>Agency Address</Label>
              <Description>Physical address of your agency.</Description>
              <Textarea
                value={form.configuration?.agency?.address || ''}
                onChange={(e) => {
                  const address = e.currentTarget.value;
                  updateForm(['configuration', 'agency', 'address'], (prev) => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      agency: { ...prev.configuration?.agency, address }
                    }
                  }));
                }}
                invalid={
                  !!getValidationError(validationErrors, ['configuration', 'agency', 'address'])
                }
                placeholder={agencyPlaceholders.mailingAddressPlaceholder}
                style={{ minHeight: '80px' }}
              />
              {!!getValidationError(validationErrors, ['configuration', 'agency', 'address']) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, ['configuration', 'agency', 'address'])}
                </ErrorMessage>
              )}
            </Field>

            <Field
              className="max-w-md"
              data-field-id={getFieldId(['configuration', 'agency', 'website'])}
            >
              <Label>Website</Label>
              <Description>
                The agency website you'd like to give to callers if they ask for it.
              </Description>
              <Input
                type="text"
                value={form.configuration?.agency?.website || ''}
                onChange={(e) => {
                  const website = e.currentTarget.value;
                  updateForm(['configuration', 'agency', 'website'], (prev) => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      agency: { ...prev.configuration?.agency, website }
                    }
                  }));
                }}
                invalid={
                  !!getValidationError(validationErrors, ['configuration', 'agency', 'website'])
                }
                placeholder="yourwebsite.com"
              />
              {!!getValidationError(validationErrors, ['configuration', 'agency', 'website']) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, ['configuration', 'agency', 'website'])}
                </ErrorMessage>
              )}
            </Field>

            <Field
              className="max-w-md"
              data-field-id={getFieldId(['configuration', 'agency', 'businessHours'])}
            >
              <Label>Business Hours</Label>
              <Description>
                When your agency is open for business. Cara will share this with callers for
                informational purposes only. This will not affect transfers.
              </Description>
              <Textarea
                value={form.configuration?.agency?.businessHours || ''}
                onChange={(e) => {
                  const businessHours = e.currentTarget.value;
                  updateForm(['configuration', 'agency', 'businessHours'], (prev) => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      agency: { ...prev.configuration?.agency, businessHours }
                    }
                  }));
                }}
                invalid={
                  !!getValidationError(validationErrors, [
                    'configuration',
                    'agency',
                    'businessHours'
                  ])
                }
                placeholder="Monday - Friday: 8:00 AM - 5:00 PM&#10;Saturday: 9:00 AM - 1:00 PM&#10;Sunday: Closed"
                style={{ minHeight: '80px' }}
              />
              {!!getValidationError(validationErrors, [
                'configuration',
                'agency',
                'businessHours'
              ]) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, [
                    'configuration',
                    'agency',
                    'businessHours'
                  ])}
                </ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
      </div>
    </div>
  </div>
);

export const VoiceAgentCallBehaviorSection = ({
  form,
  validationErrors,
  getFieldId,
  getValidationError,
  updateForm,
  hasAdvancedCustomization = false,
  isOutboundVoice = false,
  voice35Enabled = false
}: VoiceAgentFormSectionProps) => {
  const lineOfBusinessRequired = Boolean(
    voice35Enabled &&
    transferDirectoryUsesLineOfBusinessRouting(form.configuration.transfer.directory)
  );
  const visibleRequiredContactInfoOptions = voice35Enabled
    ? requiredContactInfoOptions
    : requiredContactInfoOptions.filter(
        (option) => option.value !== REQUIRED_CONTACT_INFO_LINE_OF_BUSINESS
      );

  return (
    <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-6 sm:px-6">
        <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
          <div>
            <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
              Call Behavior
            </h4>
            <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
              Configure how Cara behaves during calls and what information she collects.
            </p>
          </div>

          <Fieldset className="flex flex-col gap-y-6 col-span-2">
            <FieldGroup>
              <Field
                data-field-id={getFieldId(['configuration', 'callBehavior', 'rapportBuilding'])}
              >
                <Label>Communication Style</Label>
                <Description>How Cara approaches the conversation.</Description>
                <RadioGroup
                  value={form.configuration?.callBehavior?.rapportBuilding}
                  onChange={(value) => {
                    updateForm(['configuration', 'callBehavior', 'rapportBuilding'], (prev) => ({
                      ...prev,
                      configuration: {
                        ...prev.configuration,
                        callBehavior: {
                          ...prev.configuration?.callBehavior,
                          rapportBuilding:
                            value as VoiceAgentConfigurationCallBehaviorRapportBuilding
                        }
                      }
                    }));
                  }}
                >
                  <RadioField>
                    <Radio
                      color="sky"
                      value={VoiceAgentConfigurationCallBehaviorRapportBuilding.BuildRapport}
                    />
                    <Label>Build Rapport</Label>
                    <Description>
                      Cara actively engages in friendly conversation dynamically asking additional
                      empathetic and inquisitive questions to learn more about the caller.
                    </Description>
                  </RadioField>
                  <RadioField>
                    <Radio
                      color="sky"
                      value={VoiceAgentConfigurationCallBehaviorRapportBuilding.Neutral}
                    />
                    <Label>Friendly</Label>
                    <Description>
                      Cara is friendly and often mirrors the client responses but doesn't actively
                      build rapport.
                    </Description>
                  </RadioField>
                  <RadioField>
                    <Radio
                      color="sky"
                      value={VoiceAgentConfigurationCallBehaviorRapportBuilding.Cordial}
                    />
                    <Label>Cordial</Label>
                    <Description>
                      Cara maintains professional warmth with natural, varied responses.
                    </Description>
                  </RadioField>
                  <RadioField>
                    <Radio
                      color="sky"
                      value={VoiceAgentConfigurationCallBehaviorRapportBuilding.TaskFocused}
                    />
                    <Label>Task Focused</Label>
                    <Description>
                      Cara responds concisely and stays strictly focused on completing the task.
                    </Description>
                  </RadioField>
                </RadioGroup>
                {!!getValidationError(validationErrors, [
                  'configuration',
                  'callBehavior',
                  'rapportBuilding'
                ]) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, [
                      'configuration',
                      'callBehavior',
                      'rapportBuilding'
                    ])}
                  </ErrorMessage>
                )}
              </Field>

              {!isOutboundVoice && (
                <>
                  <Field
                    data-field-id={getFieldId([
                      'configuration',
                      'callBehavior',
                      'requiredContactInfo'
                    ])}
                  >
                    <Label>Required Contact Information</Label>
                    <Description>
                      Contact information that Cara must collect from every caller. Cara will always
                      attempt to collect their name.
                    </Description>
                    <CheckboxGroup>
                      {visibleRequiredContactInfoOptions.map((option) => {
                        const isLockedLineOfBusiness =
                          option.value === REQUIRED_CONTACT_INFO_LINE_OF_BUSINESS &&
                          lineOfBusinessRequired;
                        return (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={isLockedLineOfBusiness}
                              checked={
                                isLockedLineOfBusiness ||
                                form.configuration?.callBehavior?.requiredContactInfo?.includes(
                                  option.value
                                ) ||
                                false
                              }
                              onChange={(checked) => {
                                if (isLockedLineOfBusiness) {
                                  return;
                                }
                                updateForm(
                                  ['configuration', 'callBehavior', 'requiredContactInfo'],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.callBehavior?.requiredContactInfo || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        callBehavior: {
                                          ...prev.configuration?.callBehavior,
                                          requiredContactInfo: updated
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                            <Description>{option.description}</Description>
                          </CheckboxField>
                        );
                      })}
                    </CheckboxGroup>
                    {voice35Enabled && (
                      <div className="mt-6 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4 dark:border-neutral-700 dark:bg-neutral-900/40">
                        <p className="m-0 text-sm font-medium text-neutral-900 dark:text-white">
                          Additional Required Information
                        </p>
                        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                          Add custom fields Cara must collect from every caller (one per line), such
                          as policy type or policy number.
                        </p>
                        <Textarea
                          className="mt-4"
                          disabled={!hasAdvancedCustomization}
                          value={
                            form.configuration?.callBehavior?.customRequiredContactInfo?.join(
                              '\n'
                            ) || ''
                          }
                          onChange={(e) => {
                            const customRequiredContactInfo = e.currentTarget.value.split('\n');
                            updateForm(
                              ['configuration', 'callBehavior', 'customRequiredContactInfo'],
                              (prev) => ({
                                ...prev,
                                configuration: {
                                  ...prev.configuration,
                                  callBehavior: {
                                    ...prev.configuration?.callBehavior,
                                    customRequiredContactInfo
                                  }
                                }
                              })
                            );
                          }}
                          placeholder="Policy type&#10;Policy number"
                          style={{ minHeight: '80px' }}
                        />
                        {!!getValidationError(validationErrors, [
                          'configuration',
                          'callBehavior',
                          'customRequiredContactInfo'
                        ]) && (
                          <ErrorMessage>
                            {getValidationError(validationErrors, [
                              'configuration',
                              'callBehavior',
                              'customRequiredContactInfo'
                            ])}
                          </ErrorMessage>
                        )}
                      </div>
                    )}
                    {!!getValidationError(validationErrors, [
                      'configuration',
                      'callBehavior',
                      'requiredContactInfo'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'configuration',
                          'callBehavior',
                          'requiredContactInfo'
                        ])}
                      </ErrorMessage>
                    )}
                  </Field>

                  <SwitchField
                    data-field-id={getFieldId([
                      'configuration',
                      'callBehavior',
                      'repeatCriticalInfo'
                    ])}
                  >
                    <Switch
                      color="sky"
                      checked={form.configuration?.callBehavior?.repeatCriticalInfo || false}
                      onChange={(enabled) => {
                        updateForm(
                          ['configuration', 'callBehavior', 'repeatCriticalInfo'],
                          (prev) => ({
                            ...prev,
                            configuration: {
                              ...prev.configuration,
                              callBehavior: {
                                ...prev.configuration?.callBehavior,
                                repeatCriticalInfo: enabled
                              }
                            }
                          })
                        );
                      }}
                    />
                    <Label>Repeat Critical Information</Label>
                    <Description>
                      Cara will repeat back critical information to the caller such as caller's
                      name, email address, phone, business name, and policy numbers.
                    </Description>
                  </SwitchField>

                  <Field
                    className="max-w-md"
                    data-field-id={getFieldId(['configuration', 'callBehavior', 'startSpeaker'])}
                  >
                    <Label>Start Speaker</Label>
                    <Description>
                      Select who should speak first when the call begins. If set to 'User', Cara
                      will wait for the caller to speak first.
                    </Description>
                    <Select
                      value={
                        form.configuration?.callBehavior?.startSpeaker ||
                        VoiceAgentStartSpeaker.Agent
                      }
                      onChange={(e) => {
                        const value = e.currentTarget.value as VoiceAgentStartSpeaker;
                        updateForm(['configuration', 'callBehavior', 'startSpeaker'], (prev) => ({
                          ...prev,
                          configuration: {
                            ...prev.configuration,
                            callBehavior: {
                              ...prev.configuration?.callBehavior,
                              startSpeaker: value,
                              ...(value === VoiceAgentStartSpeaker.User
                                ? {
                                    beginAfterUserSilenceMs:
                                      prev.configuration?.callBehavior?.beginAfterUserSilenceMs ??
                                      5000
                                  }
                                : {
                                    beginAfterUserSilenceMs: undefined
                                  })
                            }
                          }
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, [
                          'configuration',
                          'callBehavior',
                          'startSpeaker'
                        ])
                      }
                    >
                      <option value={VoiceAgentStartSpeaker.Agent}>Agent</option>
                      <option value={VoiceAgentStartSpeaker.User}>User</option>
                    </Select>
                    {!!getValidationError(validationErrors, [
                      'configuration',
                      'callBehavior',
                      'startSpeaker'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'configuration',
                          'callBehavior',
                          'startSpeaker'
                        ])}
                      </ErrorMessage>
                    )}
                  </Field>

                  {form.configuration?.callBehavior?.startSpeaker ===
                    VoiceAgentStartSpeaker.User && (
                    <VoiceDiscreteSlider
                      data-field-id={getFieldId([
                        'configuration',
                        'callBehavior',
                        'beginAfterUserSilenceMs'
                      ])}
                      className="max-w-md"
                      label="Begin After User Silence"
                      description="How long to wait (in seconds) after detecting user silence before the agent begins speaking at the beginning of the call. 'None' will mean the agent will wait indefinitely for the user to speak."
                      options={beginAfterUserSilenceOptions}
                      value={form.configuration?.callBehavior?.beginAfterUserSilenceMs || 0}
                      onChange={(beginAfterUserSilenceMs) => {
                        updateForm(
                          ['configuration', 'callBehavior', 'beginAfterUserSilenceMs'],
                          (prev) => ({
                            ...prev,
                            configuration: {
                              ...prev.configuration,
                              callBehavior: {
                                ...prev.configuration?.callBehavior,
                                beginAfterUserSilenceMs:
                                  beginAfterUserSilenceMs === 0
                                    ? undefined
                                    : beginAfterUserSilenceMs
                              }
                            }
                          })
                        );
                      }}
                      error={
                        getValidationError(validationErrors, [
                          'configuration',
                          'callBehavior',
                          'beginAfterUserSilenceMs'
                        ]) || undefined
                      }
                    />
                  )}
                </>
              )}

              <SwitchField className="hidden">
                <Switch
                  color="sky"
                  checked={form.configuration?.callBehavior?.referencePolicyInfo || false}
                  onChange={(enabled) => {
                    updateForm(
                      ['configuration', 'callBehavior', 'referencePolicyInfo'],
                      (prev) => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          callBehavior: {
                            ...prev.configuration?.callBehavior,
                            referencePolicyInfo: enabled
                          }
                        }
                      })
                    );
                  }}
                />
                <Label>Reference Policy Information</Label>
                <Description>
                  Cara will look up and reference the caller's policy information if requested over
                  the phone and your AMS is connected.
                </Description>
              </SwitchField>
            </FieldGroup>
          </Fieldset>
        </div>
      </div>
    </div>
  );
};

export const VoiceAgentVoiceModelSection = ({
  form,
  validationErrors,
  getFieldId,
  getValidationError,
  updateForm,
  filteredVoiceOptions,
  languageSupport,
  hasAdvancedCustomization = false,
  isOutboundVoice = false
}: VoiceAgentFormSectionProps) => (
  <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
    <div className="px-4 py-6 sm:px-6">
      <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
        <div>
          <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
            Voice & Model Settings
          </h4>
          <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
            Configure Cara's voice characteristics and model parameters.
          </p>
        </div>

        <Fieldset className="flex flex-col gap-y-6 col-span-2">
          <FieldGroup>
            <Field
              className="max-w-md"
              data-field-id={getFieldId(['configuration', 'model', 'voice'])}
            >
              <Label>Voice Selection</Label>
              <Description>Choose the voice personality for Cara.</Description>
              <div data-slot="control" className="flex justify-between gap-x-4 items-center">
                <div className="w-full">
                  <Combobox
                    value={form.configuration?.model?.voice}
                    options={filteredVoiceOptions}
                    onChange={(value) => {
                      const result = applyVoiceSelectionChange(
                        languageSupport,
                        isOutboundVoice
                          ? form.configuration.languages
                          : (form.configuration?.languages ?? []),
                        isOutboundVoice
                          ? form.configuration.model.voice
                          : (form.configuration?.model?.voice ??
                              VoiceAgentConfigurationModelVoice.Grace),
                        value as VoiceAgentConfigurationModelVoice
                      );
                      updateForm(['configuration', 'model', 'voice'], (prev) => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          languages: result.languages,
                          model: {
                            ...prev.configuration?.model,
                            voice: result.voice
                          }
                        }
                      }));
                    }}
                    error={
                      getValidationError(validationErrors, ['configuration', 'model', 'voice']) ||
                      undefined
                    }
                    placeholder="Select a voice..."
                  />
                </div>
                <SpeakerWaveIcon
                  role="button"
                  className="shrink-0 size-5 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white cursor-pointer"
                  onClick={() => {
                    const audio = new Audio(`/audio/${form.configuration?.model?.voice}.wav`);
                    audio.play();
                  }}
                />
              </div>
            </Field>

            <VoiceDiscreteSlider
              data-field-id={getFieldId(['configuration', 'model', 'responsiveness'])}
              className="max-w-md"
              label="Responsiveness"
              description="How quickly Cara responds after the caller stops speaking."
              options={responsivenessOptions}
              value={form.configuration?.model?.responsiveness || 0.9}
              onChange={(responsiveness) => {
                updateForm(['configuration', 'model', 'responsiveness'], (prev) => ({
                  ...prev,
                  configuration: {
                    ...prev.configuration,
                    model: { ...prev.configuration?.model, responsiveness }
                  }
                }));
              }}
              error={
                getValidationError(validationErrors, [
                  'configuration',
                  'model',
                  'responsiveness'
                ]) || undefined
              }
            />

            <SwitchField
              data-field-id={getFieldId(['configuration', 'model', 'enableDynamicResponsiveness'])}
            >
              <Switch
                color="sky"
                checked={form.configuration?.model?.enableDynamicResponsiveness || false}
                disabled={!hasAdvancedCustomization}
                onChange={(enabled) => {
                  updateForm(['configuration', 'model', 'enableDynamicResponsiveness'], (prev) => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      model: {
                        ...prev.configuration?.model,
                        enableDynamicResponsiveness: enabled
                      }
                    }
                  }));
                }}
              />
              <Label>
                Dynamic Responsiveness
                {!hasAdvancedCustomization && <ProLabel />}
              </Label>
              <Description>
                Dynamically adjust how quickly Cara responds based on the caller's speech rate and
                turn-taking behavior.
              </Description>
            </SwitchField>

            <VoiceDiscreteSlider
              className="max-w-md"
              label="Voice Speed"
              description="How fast or slow Cara speaks."
              options={voiceSpeedOptions}
              value={form.configuration?.model?.voiceSpeed || 1.0}
              onChange={(voiceSpeed) => {
                updateForm(['configuration', 'model', 'voiceSpeed'], (prev) => ({
                  ...prev,
                  configuration: {
                    ...prev.configuration,
                    model: { ...prev.configuration?.model, voiceSpeed }
                  }
                }));
              }}
              error={
                getValidationError(validationErrors, ['configuration', 'model', 'voiceSpeed']) ||
                undefined
              }
            />

            <SwitchField
              data-field-id={getFieldId(['configuration', 'model', 'enableDynamicVoiceSpeed'])}
            >
              <Switch
                color="sky"
                checked={form.configuration?.model?.enableDynamicVoiceSpeed || false}
                disabled={!hasAdvancedCustomization}
                onChange={(enabled) => {
                  updateForm(['configuration', 'model', 'enableDynamicVoiceSpeed'], (prev) => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      model: {
                        ...prev.configuration?.model,
                        enableDynamicVoiceSpeed: enabled
                      }
                    }
                  }));
                }}
              />
              <Label>
                Dynamic Voice Speed
                {!hasAdvancedCustomization && <ProLabel />}
              </Label>
              <Description>
                Dynamically adjust Cara's speaking speed based on the caller's speech rate and
                conversation context.
              </Description>
            </SwitchField>

            <VoiceDiscreteSlider
              data-field-id={getFieldId(['configuration', 'model', 'voiceTemperature'])}
              className="max-w-md"
              label="Voice Temperature"
              description="The variation and expressiveness of Cara's responses."
              options={voiceTemperatureOptions}
              value={form.configuration?.model?.voiceTemperature || 1.0}
              onChange={(voiceTemperature) => {
                updateForm(['configuration', 'model', 'voiceTemperature'], (prev) => ({
                  ...prev,
                  configuration: {
                    ...prev.configuration,
                    model: { ...prev.configuration?.model, voiceTemperature }
                  }
                }));
              }}
              error={
                getValidationError(validationErrors, [
                  'configuration',
                  'model',
                  'voiceTemperature'
                ]) || undefined
              }
            />

            <VoiceDiscreteSlider
              data-field-id={getFieldId(['configuration', 'model', 'voiceVolume'])}
              className="max-w-md"
              label="Voice Volume"
              description="How loud or quiet Cara speaks."
              options={voiceVolumeOptions}
              value={form.configuration?.model?.voiceVolume || 1.0}
              onChange={(voiceVolume) => {
                updateForm(['configuration', 'model', 'voiceVolume'], (prev) => ({
                  ...prev,
                  configuration: {
                    ...prev.configuration,
                    model: { ...prev.configuration?.model, voiceVolume }
                  }
                }));
              }}
              error={
                getValidationError(validationErrors, ['configuration', 'model', 'voiceVolume']) ||
                undefined
              }
            />
          </FieldGroup>
        </Fieldset>
      </div>
    </div>
  </div>
);

export const VoiceAgentEmailNotificationsSection = ({
  form,
  validationErrors,
  getFieldId,
  getValidationError,
  updateForm,
  hasAdvancedCustomization = false,
  isOutboundVoice = false
}: VoiceAgentFormSectionProps) => (
  <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
    <div className="px-4 py-6 sm:px-6">
      <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
        <div>
          <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
            Email Notifications
          </h4>
          <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
            Configure email notifications for Cara's call activities and routing preferences. This
            will only affect email notification routing after a call is completed.
          </p>
        </div>

        <Fieldset className="flex flex-col gap-y-6 col-span-2">
          <FieldGroup>
            <Field>
              <Label>Static Email Addresses</Label>
              <Description>
                Email addresses that will receive notifications for all Cara calls.
              </Description>

              <div className="space-y-3" data-slot="control">
                {form.configuration?.notifications?.staticEmails?.map((email, index) => (
                  <>
                    <div key={index} className="flex items-center gap-3 max-w-md">
                      <div className="flex-1">
                        <Input
                          value={email || ''}
                          onChange={(e) => {
                            const staticEmails = [
                              ...(form.configuration?.notifications?.staticEmails || [])
                            ];
                            staticEmails[index] = e.currentTarget.value;
                            updateForm(
                              ['configuration', 'notifications', 'staticEmails'],
                              (prev) => ({
                                ...prev,
                                configuration: {
                                  ...prev.configuration,
                                  notifications: {
                                    ...prev.configuration?.notifications,
                                    staticEmails
                                  }
                                }
                              })
                            );
                          }}
                          placeholder="agent@agency.com"
                          invalid={
                            !!getValidationError(validationErrors, [
                              'configuration',
                              'notifications',
                              'staticEmails',
                              index.toString()
                            ])
                          }
                        />
                      </div>
                      <Button
                        color="white"
                        onClick={() => {
                          const staticEmails = [
                            ...(form.configuration?.notifications?.staticEmails || [])
                          ];
                          staticEmails.splice(index, 1);
                          updateForm(
                            ['configuration', 'notifications', 'staticEmails'],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                notifications: {
                                  ...prev.configuration?.notifications,
                                  staticEmails
                                }
                              }
                            })
                          );
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                    {getValidationError(validationErrors, [
                      'configuration',
                      'notifications',
                      'staticEmails',
                      index.toString()
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'configuration',
                          'notifications',
                          'staticEmails',
                          index.toString()
                        ])}
                      </ErrorMessage>
                    )}
                  </>
                )) || []}

                <Button
                  color="sky"
                  onClick={() => {
                    const staticEmails = [
                      ...(form.configuration?.notifications?.staticEmails || [])
                    ];
                    staticEmails.push('');
                    updateForm(['configuration', 'notifications', 'staticEmails'], (prev) => ({
                      ...prev,
                      configuration: {
                        ...prev.configuration,
                        notifications: {
                          ...prev.configuration?.notifications,
                          staticEmails
                        }
                      }
                    }));
                  }}
                >
                  Add Email Address
                </Button>
              </div>
            </Field>

            <SwitchField
              data-field-id={getFieldId([
                'configuration',
                'notifications',
                'enableDynamicEmailRoutingToAgencyUsers'
              ])}
            >
              <Switch
                checked={
                  form.configuration?.notifications?.enableDynamicEmailRoutingToAgencyUsers || false
                }
                disabled={!hasAdvancedCustomization}
                onChange={(checked) => {
                  updateForm(
                    ['configuration', 'notifications', 'enableDynamicEmailRoutingToAgencyUsers'],
                    (prev) => ({
                      ...prev,
                      configuration: {
                        ...prev.configuration,
                        notifications: {
                          ...prev.configuration?.notifications,
                          enableDynamicEmailRoutingToAgencyUsers: checked
                        }
                      }
                    })
                  );
                }}
                color="sky"
              />
              <Label>
                Enable Dynamic Routing to Agents
                {!hasAdvancedCustomization && <ProLabel />}
              </Label>
              <Description>
                Automatically route after-call notifications to mentioned or transferred agents that
                have an active account in Cara.
              </Description>
            </SwitchField>

            {!isOutboundVoice && (
              <Field
                data-field-id={getFieldId([
                  'configuration',
                  'notifications',
                  'dynamicEmailRoutingCustomInstructions'
                ])}
              >
                <Label>
                  Dynamic Routing Instructions (optional)
                  {!hasAdvancedCustomization && <ProLabel />}
                </Label>
                <Description>
                  Custom instructions to determine how to route after-call notifications
                  dynamically. If specified, these instructions act in addition to the other routing
                  settings. This will only change the email notifications after the call.
                </Description>
                <Textarea
                  value={
                    form.configuration?.notifications?.dynamicEmailRoutingCustomInstructions || ''
                  }
                  disabled={!hasAdvancedCustomization}
                  onChange={(e) => {
                    const dynamicEmailRoutingCustomInstructions = e.currentTarget.value;
                    updateForm(
                      ['configuration', 'notifications', 'dynamicEmailRoutingCustomInstructions'],
                      (prev) => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          notifications: {
                            ...prev.configuration?.notifications,
                            dynamicEmailRoutingCustomInstructions
                          }
                        }
                      })
                    );
                  }}
                  placeholder="Route to claims agents for FNOL calls, route to sales agents for quote requests."
                  style={{ minHeight: '80px' }}
                  invalid={
                    !!getValidationError(validationErrors, [
                      'configuration',
                      'notifications',
                      'dynamicEmailRoutingCustomInstructions'
                    ])
                  }
                />
                {getValidationError(validationErrors, [
                  'configuration',
                  'notifications',
                  'dynamicEmailRoutingCustomInstructions'
                ]) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, [
                      'configuration',
                      'notifications',
                      'dynamicEmailRoutingCustomInstructions'
                    ])}
                  </ErrorMessage>
                )}
              </Field>
            )}
          </FieldGroup>
        </Fieldset>
      </div>
    </div>
  </div>
);

export const VoiceAgentOutboundCallBehaviorSection = ({
  form,
  validationErrors,
  getValidationError,
  updateForm
}: VoiceAgentFormSectionProps) => {
  const leaveVoicemail = form.configuration?.outboundCallBehavior?.leaveVoicemail ?? false;
  const voicemailMessage = form.configuration?.outboundCallBehavior?.voicemailMessage ?? '';
  const verifyIdentity = form.configuration?.outboundCallBehavior?.verifyIdentity ?? false;

  const patchOutboundCallBehavior = (
    path: string,
    patch: Partial<{
      leaveVoicemail: boolean;
      voicemailMessage: string;
      verifyIdentity: boolean;
    }>
  ) => {
    updateForm(['configuration', 'outboundCallBehavior', path], (prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        outboundCallBehavior: {
          leaveVoicemail: prev.configuration.outboundCallBehavior?.leaveVoicemail ?? false,
          voicemailMessage: prev.configuration.outboundCallBehavior?.voicemailMessage ?? '',
          verifyIdentity: prev.configuration.outboundCallBehavior?.verifyIdentity ?? false,
          ...patch
        }
      }
    }));
  };

  return (
    <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-6 sm:px-6">
        <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
          <div>
            <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
              Outbound Call Behavior
            </h4>
            <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
              Default behavior for outbound notification calls.
            </p>
          </div>
          <Fieldset className="col-span-2 flex flex-col gap-y-6">
            <SwitchField>
              <Label>Verify identity on call</Label>
              <Description>
                When enabled, Cara confirms the customer&apos;s identity before discussing details.
              </Description>
              <Switch
                color="sky"
                checked={verifyIdentity}
                onChange={(checked) => {
                  patchOutboundCallBehavior('verifyIdentity', { verifyIdentity: checked });
                }}
              />
            </SwitchField>
            <SwitchField>
              <Label>Leave voicemail if no answer</Label>
              <Description>
                When enabled, Cara leaves your configured message if the customer does not pick up.
              </Description>
              <Switch
                color="sky"
                checked={leaveVoicemail}
                onChange={(checked) => {
                  patchOutboundCallBehavior('leaveVoicemail', { leaveVoicemail: checked });
                }}
              />
            </SwitchField>
            {leaveVoicemail && (
              <Field>
                <Label>Voicemail message</Label>
                <Description>What Cara should say when the call goes to voicemail.</Description>
                <Textarea
                  rows={4}
                  value={voicemailMessage}
                  onChange={(e) => {
                    patchOutboundCallBehavior('voicemailMessage', {
                      voicemailMessage: e.currentTarget.value
                    });
                  }}
                  invalid={
                    !!getValidationError(validationErrors, [
                      'configuration',
                      'outboundCallBehavior',
                      'voicemailMessage'
                    ])
                  }
                  placeholder="Hi, this is Cara calling from your insurance agency about an important update on your policy. Please call us back at your earliest convenience."
                />
                {!!getValidationError(validationErrors, [
                  'configuration',
                  'outboundCallBehavior',
                  'voicemailMessage'
                ]) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, [
                      'configuration',
                      'outboundCallBehavior',
                      'voicemailMessage'
                    ])}
                  </ErrorMessage>
                )}
              </Field>
            )}
          </Fieldset>
        </div>
      </div>
    </div>
  );
};
